/** Additive, versioned contracts shared by capture, recon, and frozen reports. */
export const RECON_FEE_ACCOUNTING_VERSION = 1 as const;
export const RECON_FEE_ACCOUNTING_FLAG = 'banker_recon_lender_paid_fees';
export const FEE_PAYERS = ['borrower', 'seller', 'broker', 'lender', 'other'] as const;
export type FeePayer = typeof FEE_PAYERS[number];

export interface FeePaymentAmounts {
  atClosing: number;
  outsideClosing: number;
  total: number;
}

export interface ReconCapturedFee {
  sourceFeeKey: string;
  line: string;
  name: string;
  legacyName: string;
  section: 'A' | 'B';
  paidTo: string;
  paidToType: string;
  payments: Partial<Record<FeePayer, FeePaymentAmounts>>;
  borrowerOwnFunds: number | null;
  borrowerFinanced: number | null;
  totalAmount: number | null;
  fieldIds: Record<string, string>;
}

export interface ReconFeeCapture {
  version: typeof RECON_FEE_ACCOUNTING_VERSION;
  status: 'complete' | 'invalid' | 'stale';
  capturedAt: string;
  signature: string;
  issues: string[];
  fees: ReconCapturedFee[];
  credits: { nonSpecific: number; lenderPaid: number; cure: number };
}

export interface ReconFeeSource {
  sourceFeeKey: string;
  /** Identifies a payer/timing allocation independently of the display name. */
  portionKey: string;
  payer: FeePayer;
  timing: 'PAC' | 'POC';
  owner: 'encompass' | 'manual' | 'doc_order';
  docOrderId?: string;
  docOrderFeeId?: string;
  docOrderSplitId?: string;
}

export interface ReconFeeAccountingSnapshot {
  version: typeof RECON_FEE_ACCOUNTING_VERSION;
  capture: ReconFeeCapture;
  docOrderSignature: string;
  lenderCreditTimingBasis: 'total';
  retainedClosingFeeBasis: 'expandedLeFees';
  issues: string[];
}

export function readReconFeeAccounting(snapshot: unknown): ReconFeeAccountingSnapshot | null {
  if (!snapshot || typeof snapshot !== 'object') {
    return null;
  }
  const value = (snapshot as Record<string, unknown>).banker_recon_fee_accounting;
  if (!value || typeof value !== 'object') {
    return null;
  }
  const candidate = value as ReconFeeAccountingSnapshot;
  return candidate.version === RECON_FEE_ACCOUNTING_VERSION && candidate.capture?.version === RECON_FEE_ACCOUNTING_VERSION
    ? candidate
    : null;
}
